/**
 * Auth Store
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Company, Employee, UserProfile, LineProfile } from '../types';

export type AuthStatus = 'loading' | 'authenticated' | 'not_bound' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  employee: Employee | null;
  userProfile: UserProfile | null;
  lineProfile: LineProfile | null;
  permissions: string[];
  companies: Company[];
  currentCompanyId: number | null;

  // Actions
  setStatus: (status: AuthStatus) => void;
  setEmployee: (employee: Employee | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setLineProfile: (profile: LineProfile | null) => void;
  setAuthenticated: (profile: UserProfile, lineProfile: LineProfile) => void;
  setNotBound: (lineProfile: LineProfile) => void;
  setCompany: (id: number) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

// keep the persisted currentCompanyId unless it's missing or no longer in the
// server's allowed-companies list for this user
function resolveCurrentCompanyId(
  persisted: number | null,
  profile: UserProfile
): number {
  if (persisted !== null && profile.companies.some((c) => c.id === persisted)) {
    return persisted;
  }
  return profile.current_company_id;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      status: 'loading',
      employee: null,
      userProfile: null,
      lineProfile: null,
      permissions: [],
      companies: [],
      currentCompanyId: null,

      setStatus: (status) => set({ status }),

      setEmployee: (employee) => set({ employee }),

      setUserProfile: (profile) =>
        set({
          userProfile: profile,
          employee: profile?.employee ?? null,
          permissions: profile?.permissions ?? [],
          companies: profile?.companies ?? [],
          currentCompanyId: profile
            ? resolveCurrentCompanyId(get().currentCompanyId, profile)
            : null,
        }),

      setLineProfile: (profile) => set({ lineProfile: profile }),

      setAuthenticated: (profile, lineProfile) =>
        set({
          status: 'authenticated',
          userProfile: profile,
          employee: profile.employee,
          lineProfile,
          permissions: profile.permissions,
          companies: profile.companies,
          currentCompanyId: resolveCurrentCompanyId(get().currentCompanyId, profile),
        }),

      setNotBound: (lineProfile) =>
        set({
          status: 'not_bound',
          lineProfile,
          employee: null,
          userProfile: null,
          permissions: [],
        }),

      setCompany: (id) => set({ currentCompanyId: id }),

      logout: () =>
        set({
          status: 'unauthenticated',
          employee: null,
          userProfile: null,
          lineProfile: null,
          permissions: [],
          companies: [],
          currentCompanyId: null,
        }),

      hasPermission: (permission) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },
    }),
    {
      name: 'saiens-auth',
      partialize: (state) => ({
        employee: state.employee,
        permissions: state.permissions,
        currentCompanyId: state.currentCompanyId,
      }),
    }
  )
);
